import { Download } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_7a187c08-1566-4a5d-94a6-902036d37fa8/artifacts/oin9oc2t_Untitled%20design%20%2814%29.png";
const BROCHURE_URL = "https://customer-assets.emergentagent.com/job_poly-laundry-pro/artifacts/d7wpusxn_0-2.png";
const BUILDING_IMG = "https://customer-assets.emergentagent.com/job_poly-laundry-pro/artifacts/b5es0hlx_att.vBedXlPyhABcT1sYeX7VDQK148nUOQMXtIyqY5_qkaE.jpeg";
const VAN_IMG = "https://customer-assets.emergentagent.com/job_poly-laundry-pro/artifacts/pc2fgoq4_IMG_2926.jpeg";
const AWARD_BEST = "https://customer-assets.emergentagent.com/job_poly-laundry-pro/artifacts/one50xlg_IMG_3492.jpeg";
const AWARD_COMMUNITY = "https://customer-assets.emergentagent.com/job_poly-laundry-pro/artifacts/qyavajd1_IMG_3490.jpeg";

export default function AboutSection() {
  return (
    <section id="about" className="py-24 md:py-32 bg-poly-bg" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Content */}
          <div className="reveal">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-white rounded-full p-1 shadow-md ring-1 ring-slate-100">
                <img src={LOGO_URL} alt="Poly Clean Center" className="w-8 h-8 object-contain" />
              </div>
              <span className="text-poly-blue-light font-semibold text-sm tracking-wider uppercase">About Us</span>
            </div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-poly-blue-dark tracking-tight mb-6">
              Serving Mountain Home{' '}
              <span className="text-poly-blue">Since 1963</span>
            </h2>
            <div className="space-y-4 text-poly-muted text-base leading-relaxed">
              <p>
                Poly Clean Center has been in the laundry service industry for over 60 years. 
                We have proudly served the local residents and visitors of the Twin Lakes area 
                with all their laundry needs — from everyday household loads to specialized 
                commercial accounts.
              </p>
              <p>
                We dedicate ourselves to delivering high-quality laundry services. Our goal is 
                to simplify your laundry experience, making it as convenient and stress-free as 
                possible so you can focus on what matters most.
              </p>
              <p>
                Whether you need a quick self-service wash, a full wash-dry-fold drop-off, 
                scheduled pickup and delivery, or reliable commercial laundry service for your 
                business — Poly Clean Center is here for you.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-8 mb-8">
              <div>
                <span className="font-heading font-bold text-3xl text-poly-blue">60+</span>
                <p className="text-poly-muted text-sm mt-1">Years Serving<br/>Mountain Home</p>
              </div>
              <div>
                <span className="font-heading font-bold text-3xl text-poly-orange">Free</span>
                <p className="text-poly-muted text-sm mt-1">Pickup &<br/>Delivery</p>
              </div>
              <div>
                <span className="font-heading font-bold text-3xl text-poly-green">24hr</span>
                <p className="text-poly-muted text-sm mt-1">Fast<br/>Turnaround</p>
              </div>
            </div>

            {/* Brochure Download */}
            <a
              href={BROCHURE_URL}
              download="PolyCleanCenter-Brochure.png"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white border-2 border-poly-blue text-poly-blue hover:bg-poly-blue hover:text-white font-semibold px-6 py-3 rounded-full transition-all"
              data-testid="download-brochure-btn"
            >
              <Download className="w-4 h-4" />
              Download Our Brochure
            </a>
          </div>

          {/* Right: Images */}
          <div className="reveal reveal-delay-2 space-y-6">
            <div className="relative rounded-3xl overflow-hidden shadow-xl">
              <img
                src={BUILDING_IMG}
                alt="Poly Clean Center Laundromat building exterior in Mountain Home, AR"
                className="w-full h-72 object-cover"
                loading="lazy"
              />
            </div>
            <div className="flex gap-6">
              <div className="flex-1 bg-poly-blue rounded-2xl p-6 flex flex-col items-center justify-center text-center">
                <div className="bg-white rounded-full p-2 shadow-xl mb-3">
                  <img src={LOGO_URL} alt="Poly Clean Center Logo" className="w-20 h-20 object-contain" loading="lazy" />
                </div>
                <span className="text-white font-heading font-bold text-lg">Fresh. Fast. Clean.</span>
                <span className="text-white/70 text-sm mt-1">Since 1963</span>
              </div>
              <div className="flex-1 rounded-2xl overflow-hidden">
                <img
                  src={VAN_IMG}
                  alt="Poly Clean Center delivery van for free pickup and delivery service"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Awards & Community Section */}
        <div className="mt-20 reveal">
          <div className="text-center mb-10">
            <h3 className="font-heading font-bold text-2xl sm:text-3xl text-poly-blue-dark tracking-tight">
              Awards & <span className="text-poly-blue">Community Involvement</span>
            </h3>
            <p className="text-poly-muted text-base mt-2">Recognized for excellence and proud to give back to Mountain Home.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm text-center group hover:shadow-lg transition-shadow" data-testid="award-best-laundromat">
              <div className="rounded-xl overflow-hidden mb-4">
                <img
                  src={AWARD_BEST}
                  alt="2025 Best of the Twin Lakes - Best Laundromat Award - Poly Clean Center"
                  className="w-full h-56 object-contain bg-slate-50 group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <h4 className="font-heading font-bold text-lg text-poly-blue-dark">Best Laundromat 2025</h4>
              <p className="text-poly-muted text-sm mt-1">Best of the Twin Lakes — The Baxter Bulletin</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm text-center group hover:shadow-lg transition-shadow" data-testid="award-community">
              <div className="rounded-xl overflow-hidden mb-4">
                <img
                  src={AWARD_COMMUNITY}
                  alt="2025 Community Contributor - Mountain Home Lions Club Auction"
                  className="w-full h-56 object-contain bg-slate-50 group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <h4 className="font-heading font-bold text-lg text-poly-blue-dark">Community Contributor 2025</h4>
              <p className="text-poly-muted text-sm mt-1">Mountain Home Lions Club Auction</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
